import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Youtube, Loader2, Trash2, Check, ShieldCheck, Music2 } from "lucide-react";
import { musicApi, errMsg, fmtTime } from "@/lib/musicApi";
import { AudioPlayer } from "@/components/music/AudioPlayer";

const ACCEPT = ".mp3,.wav,.m4a,.aac,.ogg,.flac,.webm,.mp4,.mov,.m4v,.opus";

// Reference audio: upload or YouTube link (falls back to upload when YouTube blocks extraction).
export const ReferenceInput = ({ refs, setRefs, selectedId, onSelect }) => {
  const [tab, setTab] = useState("upload");
  const [rights, setRights] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [yt, setYt] = useState({ url: "", start: 0, end: "" });
  const [blocked, setBlocked] = useState(null);
  const fileRef = useRef(null);
  const selected = refs.find((r) => r.id === selectedId);

  const added = (r) => { setRefs((l) => [r, ...l.filter((x) => x.id !== r.id)]); onSelect(r.id); toast.success("Reference ready"); };
  const upload = async (file) => {
    if (!file) return;
    if (!rights) { toast.error("Please confirm you have the rights to use this audio"); return; }
    setBusy(true); setPct(0);
    try { added(await musicApi.uploadReference(file, setPct)); setBlocked(null); }
    catch (e) { toast.error(errMsg(e, "Upload failed")); }
    setBusy(false);
  };
  const fromYoutube = async () => {
    if (!rights) { toast.error("Please confirm you have the rights to use this audio"); return; }
    setBusy(true); setBlocked(null);
    try {
      const r = await musicApi.youtubeReference({ url: yt.url, start: Number(yt.start) || 0, end: yt.end ? Number(yt.end) : null });
      if (r.needs_upload) { setBlocked(r); setTab("upload"); } else added(r);
    } catch (e) { toast.error(errMsg(e, "Could not use this link")); }
    setBusy(false);
  };
  const del = async (r) => {
    if (!window.confirm(`Delete reference "${r.name}"?`)) return;
    try { await musicApi.deleteReference(r.id); setRefs((l) => l.filter((x) => x.id !== r.id)); if (selectedId === r.id) onSelect(null); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3" data-testid="reference-input">
      <div className="flex gap-1 rounded-full bg-white/5 p-1">
        {[["upload", "Upload audio", Upload], ["youtube", "YouTube link", Youtube]].map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ref-tab-${k}`}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-1.5 text-xs font-bold transition-colors ${tab === k ? "bg-white text-black" : "text-white/60 hover:text-white"}`}><I size={13} /> {l}</button>
        ))}
      </div>
      {blocked && (
        <div className="rounded-xl border border-[#FFB547]/30 bg-[#FFB547]/10 p-3 text-xs text-[#FFD08A]" data-testid="ref-youtube-blocked">
          {blocked.youtube?.title && <div className="mb-1 font-bold text-white">{blocked.youtube.title}</div>}
          {blocked.message}
        </div>
      )}
      {tab === "upload" ? (
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ref-upload-btn"
          className="flex w-full flex-col items-center gap-1 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-5 text-center transition-colors hover:border-[#C8FF4D]/50 hover:bg-[#C8FF4D]/5 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin text-[#C8FF4D]" /> : <Upload className="text-[#C8FF4D]" size={20} />}
          <span className="text-sm font-semibold text-white">{busy ? `Uploading & analysing… ${pct}%` : "Drop or choose an audio / video file"}</span>
          <span className="text-[11px] text-white/40">MP3, WAV, M4A, FLAC, OGG, MP4, MOV, WEBM · first 10 min used</span>
        </button>
      ) : (
        <div className="space-y-2">
          <input value={yt.url} onChange={(e) => setYt({ ...yt, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid="ref-youtube-url"
            className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none focus:border-[#C8FF4D]/60" />
          <div className="flex gap-2">
            <input type="number" min={0} value={yt.start} onChange={(e) => setYt({ ...yt, start: e.target.value })} placeholder="Start (s)" data-testid="ref-youtube-start" className="w-1/2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none" />
            <input type="number" min={0} value={yt.end} onChange={(e) => setYt({ ...yt, end: e.target.value })} placeholder="End (s, optional)" data-testid="ref-youtube-end" className="w-1/2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none" />
          </div>
          <button onClick={fromYoutube} disabled={busy || !yt.url.trim()} data-testid="ref-youtube-submit"
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-white/10 py-2 text-xs font-bold text-white hover:bg-white/15 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Youtube size={14} />} Check link & use as reference
          </button>
        </div>
      )}
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="ref-file-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      <label className="flex cursor-pointer items-start gap-2 text-[11px] leading-snug text-white/55" data-testid="ref-rights-label">
        <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} data-testid="ref-rights-checkbox" className="mt-0.5 accent-[#C8FF4D]" />
        <span>I own this audio or have permission to process it. Public availability isn't permission. Files stay private and are never used for training.</span>
      </label>
      {selected && <SelectedReference r={selected} />}
      {refs.length > 0 && (
        <div className="max-h-40 space-y-1 overflow-y-auto pr-1" data-testid="ref-list">
          {refs.map((r) => (
            <div key={r.id} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs ${r.id === selectedId ? "bg-[#C8FF4D]/10 text-white" : "text-white/60 hover:bg-white/5"}`}>
              <button onClick={() => onSelect(r.id === selectedId ? null : r.id)} data-testid={`ref-select-${r.id}`} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                {r.id === selectedId ? <Check size={13} className="text-[#C8FF4D]" /> : r.source === "youtube" ? <Youtube size={13} /> : <Music2 size={13} />}
                <span className="truncate">{r.name}</span><span className="ml-auto shrink-0 font-mono text-white/35">{fmtTime(r.duration)}</span>
              </button>
              <button onClick={() => del(r)} data-testid={`ref-delete-${r.id}`} className="rounded p-1 text-white/30 hover:text-red-400"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SelectedReference = ({ r }) => (
  <div className="rounded-xl border border-white/10 bg-black/40 p-3" data-testid="ref-selected">
    <div className="mb-2 flex items-center gap-1.5 text-xs font-bold text-white"><ShieldCheck size={13} className="text-[#C8FF4D]" /> <span className="truncate">{r.name}</span></div>
    <AudioPlayer src={`/music/references/${r.id}/file`} peaks={r.peaks} duration={r.duration} testId="ref-player" color="#7AD7FF" />
    <div className="mt-2 flex flex-wrap gap-1.5 font-mono text-[10px]" data-testid="ref-analysis">
      {r.analysis ? (<>
        <Chip>{r.analysis.bpm ? `${r.analysis.bpm} BPM` : "BPM —"}</Chip><Chip>{r.analysis.key}</Chip><Chip>Energy {r.analysis.energy}</Chip>
      </>) : <Chip>Analysis unavailable</Chip>}
      <Chip>{fmtTime(r.duration)}{r.trimmed ? " (trimmed)" : ""}</Chip><Chip>{r.format}</Chip>
    </div>
  </div>
);
const Chip = ({ children }) => <span className="rounded-full border border-white/10 px-2 py-0.5 text-white/60">{children}</span>;
